/*
 * Copyright (c) 2024-2025. Haodong JU
 */

import { VSelect, VTextField } from 'vuetify/lib/components'
import { AwardClaimType } from '@/model/dataLayer/api/MembershipSystem/AwardManagement'
import Asset from '@/model/dataLayer/api/MembershipSystem/Asset'

/**
 * Creates a form schema for award creation and editing with detailed fields
 *
 * @param {Object} options - Configuration options
 * @returns {Object} - Form schema object with title, subtitle, and schemas array
 */
export function createAwardFormSchema (options = {}) {
  return {
    title: options.isEdit ? '编辑奖励' : '创建奖励',
    subtitle: options.isEdit ? `编辑 ${options.award?.description || '奖励'}` : '创建新的奖励',
    schemas: [
      // Basic Information Section
      {
        key: 'description',
        name: '奖励描述',
        component: VTextField,
        tag: 'basic',
        required: true,
        hint: '奖励的名称或描述，例如"消费10次获得免费饮料"',
        componentProps: {
          placeholder: '请输入奖励描述',
          counter: 100,
          rules: [v => !!v || '描述不能为空', v => v.length <= 100 || '描述不能超过100个字符']
        }
      },
      {
        key: 'awardMax',
        name: '可兑奖数量',
        component: VTextField,
        tag: 'basic',
        required: true,
        default: 10,
        hint: '积分可以兑换奖励的最大值，例如10表示集满10分可兑换奖励',
        componentProps: {
          type: 'number',
          placeholder: '请输入可兑奖数量',
          rules: [
            v => !!v || '可兑奖数量不能为空',
            v => v > 0 || '可兑奖数量必须大于0',
            v => Number.isInteger(Number(v)) || '可兑奖数量必须是整数'
          ]
        }
      },
      {
        key: 'awardStart',
        name: '起始值',
        component: VTextField,
        tag: 'basic',
        required: false,
        default: 1,
        hint: '奖励积分的起始值，通常为1，表示从1开始累积',
        componentProps: {
          type: 'number',
          placeholder: '请输入起始值',
          rules: [
            v => v >= 0 || '起始值不能为负数',
            v => Number.isInteger(Number(v)) || '起始值必须是整数'
          ]
        }
      },
      {
        key: 'active',
        name: '激活状态',
        component: VSelect,
        tag: 'basic',
        required: false,
        default: true,
        hint: '设置奖励是否处于激活状态，只有激活的奖励才能被用户获得',
        componentProps: {
          items: [
            { text: '激活', value: true },
            { text: '未激活', value: false }
          ]
        }
      },

      // Reward Settings Section
      {
        key: 'assetId',
        name: '关联资产',
        component: VSelect,
        tag: 'reward',
        required: false,
        hint: '选择兑换奖励时获得的资产，如优惠券、代金券等',
        componentProps: {
          items: [],
          itemText: 'name',
          itemValue: 'id',
          placeholder: '请选择关联资产',
          clearable: true
        },
        async beforeMount (field) {
          try {
            const assets = await Asset.getList(true)
            field.componentProps.items = assets
          } catch (error) {
            console.error('Error loading assets:', error)
            field.componentProps.items = []
          }
        }
      },
      {
        key: 'repeatable',
        name: '可重复获得',
        component: VSelect,
        tag: 'reward',
        required: false,
        default: true,
        hint: '设置用户是否可以重复获得此奖励，例如每集满10分都可以兑换一次',
        componentProps: {
          items: [
            { text: '是', value: true },
            { text: '否', value: false }
          ]
        }
      },
      {
        key: 'allowExcessAccumulation',
        name: '超额累积',
        component: VSelect,
        tag: 'reward',
        required: false,
        default: false,
        hint: '设置是否允许积分超额累积，例如用户有12分时，兑换10分的奖励后剩余2分',
        componentProps: {
          items: [
            { text: '允许', value: true },
            { text: '不允许', value: false }
          ]
        }
      },

      // Accumulation Rules Section
      {
        key: 'claimType',
        name: '积分获取方式',
        component: VSelect,
        tag: 'rules',
        required: false,
        default: 'OrderCount',
        hint: '设置用户如何获得积分：OrderCount(一单一分)、OrderAmount(1欧一分，向下取整)、ItemCount(按商品数量)',
        componentProps: {
          items: AwardClaimType,
          itemText: 'label',
          itemValue: 'value',
          placeholder: '请选择积分获取方式'
        }
      },
      {
        key: 'minOrderAmount',
        name: '最小订单金额',
        component: VTextField,
        tag: 'rules',
        required: false,
        default: 0,
        hint: '设置获得积分的最小订单金额，例如订单金额超过10欧才能获得积分',
        componentProps: {
          type: 'number',
          placeholder: '请输入最小订单金额',
          rules: [
            v => v >= 0 || '最小订单金额不能为负数'
          ]
        }
      },
      {
        key: 'pointClearAfterLastUsageDays',
        name: '积分清除天数',
        component: VTextField,
        tag: 'rules',
        required: false,
        default: 999,
        hint: '设置积分在最后一次使用后多少天自动清除，999表示基本不清除',
        componentProps: {
          type: 'number',
          placeholder: '请输入积分清除天数',
          rules: [
            v => v >= 0 || '积分清除天数不能为负数',
            v => Number.isInteger(Number(v)) || '积分清除天数必须是整数'
          ]
        }
      }
    ]
  }
}
